import { lazy, Suspense, useMemo } from 'react'
import { LoadingPage } from 'pages/loading_page'
import { RouteDefs } from 'routes/_utils/transform_routes/transform.routes'
import { useParams } from 'react-router-dom'

/**
 * Props for the LazyRoute utility
 * @param props
 * @category Routes
 */
export type LazyRouteProps = RouteDefs

/**
 * Lazy loads a Component and shows a loading page while loading
 * @param props
 * @category Routes
 */
export function LazyRoute(props: LazyRouteProps): JSX.Element {
  const LazyComponent = lazy(props.file)
  return (
    <Suspense fallback={<LoadingPage />}>
      {props.params?.length ? (
        <LazyComponentWithParams
          component={LazyComponent}
          path={props.path ?? ''}
          params={props.params}
        />
      ) : (
        <LazyComponent />
      )}
    </Suspense>
  )
}

function LazyComponentWithParams(props: {
  component: React.FC
  path: string
  params: string[]
}): JSX.Element {
  const Component = props.component
  const urlParams = useParams()
  return useMemo(() => {
    const params: Record<string, string | undefined> = {}
    for (const param of props.params) {
      if (urlParams?.[param]) {
        params[`${param}`] = urlParams[param]
      }
    }
    return <Component {...params} />
  }, [urlParams, props.params, Component])
}
